<template lang="pug">
.account-team-users
  .nav.top-bar.my-3.font-size-title
    .d-flex.align-items-center
      router-link.pe-2(:to='{ name: "account.teams" }')
        icon(name='chevron-left')
      span {{ $t('pages.teamUsers.pageTitle') }}
  .row
    .col-12
      ul#my-tab.nav.nav-tabs(role='tablist')
        li.nav-item.active(role='presentation')
          .nav-link.active {{ teamName }}

  .row.mt-4
    .col-12
      .row
        .col-12.col-md-6
          .form-group.d-flex.flex-md-row
            .form-control-icon--end-line(style='width: 400px')
              input#name.form-control.rounded-3(
                :placeholder='$t("pages.teamUsers.searchPlaceholder")'
                autocomplete='off'
                v-model='filters.search'
              )
              icon(name='magnifying-glass')
        .col-12.col-md-6.text-end
          button.btn.btn-primary.btn--add-user(@click='addUser' v-if='isOwner')
            | {{ $t('pages.teamUsers.addUser') }}
      .row(v-if='loading')
        .col-12.text-center.mt-5
          p {{ $t('loadingText') }}
      .row(v-else-if='filteredUsers.length > 0')
        .col-12
          card-table(:total-items='filteredUsers.length' breakpoint-size='lg')
            template(
              v-for='(user, index) in filteredUsers'
              v-slot:[`card-${index}`]='{}'
            )
              .mb-1 {{ $t('usersGuestCardTable.name') }}: {{ user.name }}
              .mb-1 {{ $t('usersGuestCardTable.email') }}: {{ user.email }}
              .mb-0
                span.owner__organization(v-if='user.is_owner === true') {{ $t('usersGuestCardTable.organizationOwner') }}
                button.btn--delete-user(
                  @click.stop='openDeleteUserModal(user)'
                  v-else-if='isOwner'
                  v-tooltip:bottom='$t("usersGuestCardTable.deleteUser")'
                )
                  icon.icon--canceled(name='trash-can')
            template(v-slot:table-head)
              th(style='width: 35%') {{ $t('usersGuestCardTable.name') }}
              th(style='width: 35%') {{ $t('usersGuestCardTable.email') }}
              th(style='width: 30%')
            template(
              v-for='(user, index) in filteredUsers'
              v-slot:[`table-row-${index}`]='{}'
            )
              td {{ user.name }}
              td {{ user.email }}
              td
                .text-end
                  span.owner__organization(v-if='user.is_owner === true') {{ $t('usersGuestCardTable.organizationOwner') }}
                  button(
                    @click.stop='openDeleteUserModal(user)'
                    v-else-if='isOwner'
                    v-tooltip:bottom='$t("usersGuestCardTable.deleteUser")'
                  )
                    icon.icon--canceled(name='trash-can')
      .row(v-else-if='isSearchingUsers')
        .col-12.mt-5
          .text-center.mt-5
            img.img-fluid(
              src='/img/ilustrations/no-contacts-found.svg'
              style='max-height: 260px'
            )
            p.mt-4 {{ $t('message.no_results_found') }}
      .row(v-else)
        .col-12.text-center.mt-5
          img.img-fluid(
            src='/img/ilustrations/no-contacts-found.svg'
            style='max-height: 260px'
          )
          p.mt-4 {{ $t('message.no_results_found') }}
  delete-team-user-modal(
    :team-id='teamId'
    :user-id='selectedUserId'
    @refresh='fetchTeamUsers'
    ref='deleteTeamUserModal'
  )
  add-team-user-modal(
    :existing-user-ids='existingUserIds'
    :team-id='teamId'
    @selected-users='handleAddUsers'
    ref='addTeamUserModal'
  )
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
import CardTable from '@/ui/organisms/CardTable.vue';
import TeamsService from '@/modules/account/services/http/TeamsService';
import alert from '@/modules/account/common/alert';
import DeleteTeamUserModal from '@/modules/account/components/organisms/modals/DeleteTeamUserModal.vue';
import AddTeamUserModal from '@/modules/account/components/organisms/modals/AddTeamUserModal.vue';

export default {
  name: 'TeamUsersPage',
  components: {
    Icon,
    CardTable,
    DeleteTeamUserModal,
    AddTeamUserModal,
  },
  data() {
    return {
      teamName: '',
      teamId: '',
      users: [],
      loading: true,
      selectedUserId: '',
      addingUsers: false,
      filters: {
        search: '',
      },
    };
  },
  computed: {
    isOwner() {
      return this.$store.state.organizations.selected?.is_owner;
    },
    filteredUsers() {
      const search = this.filters.search.toLowerCase().trim();
      if (!search) return this.users;
      return this.users.filter(
        (user) =>
          (user.name && user.name.toLowerCase().includes(search)) ||
          (user.email && user.email.toLowerCase().includes(search))
      );
    },
    isSearchingUsers() {
      return this.filters.search !== '';
    },
    existingUserIds() {
      return this.users.map((u) => u.id);
    },
  },
  created() {
    this.updatePageStrings();
    this.fetchTeamUsers();
  },
  methods: {
    updatePageStrings() {
      this.$store.dispatch(
        'page/setTitle',
        this.$t('pages.teamUsers.pageTitle')
      );
      this.$store.dispatch('page/setHead', '');
      this.$store.dispatch('page/setClass', 'account teams');
    },
    async fetchTeamUsers() {
      this.loading = true;
      this.$loading(true);
      try {
        this.teamId = this.$route.params.teamId;
        const response = await TeamsService.listSpecificTeam(this.teamId);
        const team = response.data;
        this.teamName = team.name;
        this.users = team.users || [];
      } catch {
        this.$router.push({ name: 'account.teams' });
      } finally {
        this.loading = false;
        this.$loading(false);
      }
    },
    openDeleteUserModal(user) {
      this.selectedUserId = user.id;
      this.$refs.deleteTeamUserModal.$refs.modal.open();
    },
    addUser() {
      this.$refs.addTeamUserModal.$refs.modal.open();
    },
    async handleAddUsers(payload) {
      this.addingUsers = true;
      this.$loading(true);
      try {
        const data = { users: payload.users.map((u) => u.id) };
        await TeamsService.addUsers(this.teamId, data);
        this.$refs.addTeamUserModal.$refs.modal.close();
        await this.fetchTeamUsers();
        alert.fireAlert(this.$t('modal.addTeamUser.addedUsers'), {
          styles:
            'background-color: #D4EDDA; border: solid 1px #AACFB3; color: #0F6642;',
          icon: 'circle-check',
        });
      } catch (error) {
        alert.fireAlert(
          `${this.$t('modal.addTeamUser.error')} ${
            error.response?.data?.message || ''
          }`,
          {
            classes: 'alert-danger',
            styles:
              'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
            tag: 'httpAlert',
            icon: 'circle-exclamation',
          }
        );
      } finally {
        this.addingUsers = false;
        this.$loading(false);
      }
    },
  },
};
</script>

<style lang="stylus">
.account-team-users
  .top-bar
    display: flex
    align-items: center
    color: #5C7A84
    font-weight: 600
    a
      cursor: pointer
      color: #013D52
      line-height: 1em
      &:hover
        text-decoration: none
  .btn--add-user
    margin-bottom: 0
  .btn--delete-user
    position: absolute
    top: 0.5rem
    right: 0.5rem
  .owner__organization
    font-weight: 600
    color: #013D52
</style>
